import React from "react";
import {
  WelcomeScreen,
  QuestionScreen,
  TestimonialPick,
  ContactScreen,
  ThanksScreen,
} from "./screens";
import { WaveyBackground } from "./../shared/WaveyBackground";
import { BubblesBackground } from "./../shared/BubblesBackground";
import { SpeechBubblesBackground } from "./../shared/SpeechBubblesBackground";
import { StarsBackground } from "./../shared/StarsBackground";
import { RetroBackground } from "./../shared/RetroBackground";
import { ConfettiBackground } from "./../shared/ConfettiBackground";
import { GradientBackground } from "./../shared/GradientBackground";
import { DotsBackground } from "./../shared/DotsBackground";
import { DoodlesBackground } from "./../shared/DoodlesBackground";
import { SolidBackground } from "./../shared/SolidBackground";

// Same map the capform editor preview uses, and the same set the
// server-side shared/_capform_background.html.erb dispatches over.
const BACKGROUNDS = {
  wavey: WaveyBackground,
  bubbles: BubblesBackground,
  speech: SpeechBubblesBackground,
  stars: StarsBackground,
  retro: RetroBackground,
  confetti: ConfettiBackground,
  gradient: GradientBackground,
  dots: DotsBackground,
  doodles: DoodlesBackground,
  solid: SolidBackground,
};

// Right-pane live preview for the survey studio. Renders one screen at
// a time — the rich background patterns are too expensive to mirror in
// React (they're server-rendered partials in production), so we show
// the runner card against a flat tint of the chosen background color
// plus a small "fullscreen preview" affordance in the editor header.
//
// Props:
//   - survey:       the live survey object (kept in step with edits)
//   - page:         "welcome" | "thanks" | "testimonial" | { kind: "question", id }
//   - previewMode:  "desktop" | "mobile" — sets the preview card width
//   - selectedId:   currently-selected question id (used when page="question")

export default function SurveyPreview({ survey, page, previewMode = "desktop", selectedId }) {
  const data       = survey.data || {};
  const widthClass = previewMode === "mobile" ? "max-w-[380px]" : "max-w-lg";

  // The pane used to paint a flat tint and nothing else, so none of the ten
  // patterns showed and an unset colour fell back to a beige the picker
  // never offers. Render the real component instead — the same one the
  // public page gets — and default to the picker's own blue.
  const Background = BACKGROUNDS[data.background_style] || WaveyBackground;
  const bgColor    = data.background_color || "#3E8DFF";

  return (
    <div className="border-[3px] border-black bg-white shadow-[6px_6px_0_rgba(0,0,0,0.18)] overflow-hidden md:h-[calc(100vh-160px)] flex flex-col">
      {/* Same browser chrome the capform preview uses, so both editors
          frame their preview the same way. */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b-[2px] border-black bg-slate-100">
        <span className="block w-2.5 h-2.5 rounded-full bg-[#FF7AAC] border border-black/40" />
        <span className="block w-2.5 h-2.5 rounded-full bg-[#FFD23A] border border-black/40" />
        <span className="block w-2.5 h-2.5 rounded-full bg-[#3EB567] border border-black/40" />
        <div className="ml-3 flex-1 h-5 bg-white border border-black/20 rounded-sm flex items-center px-2">
          <span className="text-[10px] font-bold text-slate-400 truncate">your-site.com</span>
        </div>
      </div>

      <div className="relative w-full overflow-y-auto flex-1 min-h-[400px]">
        <Background bgColor={bgColor} />
        <div className="relative flex justify-center px-3 sm:px-6 py-8 sm:py-12">
          <div className={`${widthClass} w-full`}>
            <div className="relative w-full border-[2px] border-black overflow-hidden rounded-2xl bg-white shadow-2xl">
              <ScreenSwitch survey={survey} page={page} selectedId={selectedId} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScreenSwitch({ survey, page, selectedId }) {
  if (page === "welcome") return <WelcomeScreen survey={survey} preview />;
  if (page === "thanks") return <ThanksScreen survey={survey} preview />;
  if (page === "testimonial") return <TestimonialPick prompt={survey.testimonial_prompt} preview />;
  // hasProof mirrors testimonial_step here: the author is designing for
  // the respondent who leaves one, which is the fuller set of fields.
  if (page === "contact") return <ContactScreen survey={survey} contact={null} minimal={survey.anonymous} hasProof={survey.testimonial_step} preview />;

  // Default: render whichever question is currently selected. Fall back
  // to the first question if nothing's selected; or to a hint if the
  // survey has zero questions yet.
  const target = survey.questions.find(q => q.id === selectedId) || survey.questions[0];
  if (!target) return <EmptyHint />;

  const indexLabel = (() => {
    const idx = survey.questions.indexOf(target);
    return idx >= 0 ? `${idx + 1} of ${survey.questions.length}` : "";
  })();

  return (
    <QuestionScreen
      question={target}
      value={undefined}
      onAnswer={() => {}}
      onNext={() => {}}
      onBack={null}
      indexLabel={indexLabel}
      preview
    />
  );
}

function EmptyHint() {
  return (
    <div className="p-8 text-center">
      <p className="zz-hand text-2xl text-ssblue leading-none">empty —</p>
      <p className="text-sm text-slate-600 mt-2">Add your first question on the left to see a preview here.</p>
    </div>
  );
}
